// Error boundary around the lazy-loaded route. Its main job is recovering from
// stale-chunk crashes after a redeploy (see src/lib/chunk-reload.ts): when a
// lazy import() rejects because the hashed chunk is gone, React.lazy throws into
// the nearest boundary — here — and we reload to fetch the current build instead
// of letting the page go black. Any other render error shows a minimal retry UI.
import { Component, type ReactNode } from 'react';
import { isChunkLoadError, reloadForStaleChunk } from '../lib/chunk-reload';

interface Props {
  children: ReactNode;
  /** Shown briefly while a stale-chunk reload is in flight (the route's loader). */
  loadingFallback: ReactNode;
}

export class RouteErrorBoundary extends Component<Props, { error: unknown }> {
  state = { error: null as unknown };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown) {
    // Stale chunk after a deploy → reload to the current build (guarded so it
    // can't loop). Other errors fall through to the retry UI in render().
    if (isChunkLoadError(error)) reloadForStaleChunk();
  }

  render() {
    const { error } = this.state;
    if (error != null) {
      // A reload is already in flight — show the neutral route loader so the user
      // sees a brief "loading" flash rather than an error before the page swaps.
      if (isChunkLoadError(error)) return this.props.loadingFallback;
      return (
        <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 24 }}>
          <div style={{ textAlign: 'center', color: 'var(--text-2)' }}>
            <div className="mono" style={{ fontSize: 12, letterSpacing: '0.08em', marginBottom: 12 }}>
              SOMETHING WENT WRONG LOADING THIS PAGE
            </div>
            <button className="btn" onClick={() => window.location.reload()}>Reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
